import React from 'react'
import difference from 'lodash/difference'
import includes from 'lodash/includes'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import union from 'lodash/union'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { PivotMember } from '../pivot/types'
import type { PivotFilterFormValues } from '../pivot/pivotFilterForm.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { Field } from '@ztechapps/webui.form'
import { CheckboxInput, TextInput } from '@ztechapps/webui.input'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { PivotDayInput } from './PivotFilterFields'

/** Props for the Selection pane of the filter dialog. */
export type PivotFilterSelectionPaneProps = {
  members: PivotMember[]
  /** Whether the field is an ungrouped date, which also filters by a day range. */
  showRange: boolean
}

/** The Selection kind — a searchable member checklist, with From and To days for an ungrouped date. */
export const PivotFilterSelectionPane = ({ members, showRange }: PivotFilterSelectionPaneProps) => {
  const { values, setFieldValue } = useFormikContext<PivotFilterFormValues>()
  const { search, unchecked, rangeFrom, rangeTo } = values.selection
  const visible = members.filter((member) => toLower(member.label).includes(toLower(search)))
  const visibleKeys = map(visible, 'key')
  const setUnchecked = (next: string[]) => setFieldValue('selection.unchecked', next)

  return (
    <Stack gap={2}>
      <TextInput
        placeholder="Search"
        value={search}
        onChange={(event) => setFieldValue('selection.search', event.target.value)}
      />
      <ShowIf condition={showRange}>
        <Field
          name="selection.rangeFrom"
          label="From"
          controlId="pivot-range-from"
          component={PivotDayInput}
          onChange
          max={rangeTo || undefined}
        />
        <Field
          name="selection.rangeTo"
          label="To"
          controlId="pivot-range-to"
          component={PivotDayInput}
          onChange
          min={rangeFrom || undefined}
          help="Rows with no date are left out of the range."
          showHelpAsInline
        />
      </ShowIf>
      <HorizontalStack gap={2}>
        <BusyButton text="Select all" onClick={() => setUnchecked(difference(unchecked, visibleKeys))} />
        <BusyButton text="Clear members" onClick={() => setUnchecked(union(unchecked, visibleKeys))} />
      </HorizontalStack>
      {/* capped on its own so the dialog footer stays reachable without scrolling past every member first */}
      <Stack gap={2} className="overflow-y-auto" style={{ maxHeight: '260px' }}>
        {map(visible, (member) => (
          <CheckboxInput
            key={member.key}
            // The capped member list is a flex column, and Bootstrap pins .form-check
            // to an explicit min-height — together the rows shrink to one line and
            // wrapped labels paint over each other, so each row opts out of shrinking.
            className="flex-shrink-0"
            label={member.label}
            checked={!includes(unchecked, member.key)}
            onChange={(set: boolean) =>
              setUnchecked(set ? difference(unchecked, [member.key]) : union(unchecked, [member.key]))
            }
          />
        ))}
      </Stack>
    </Stack>
  )
}
